import { Controller } from "@hotwired/stimulus"

// Custom-field definition form: shows the dropdown-options textarea only for the dropdown
// type, and shows exactly one "Standardwert" widget — the one matching the selected field
// type. The other widgets are disabled so a single default_value parameter is submitted.
// For dropdown fields the default <select> is rebuilt from the textarea lines as they are
// typed, so the admin can only pick an option that exists.
// Everything inside a widget that carries its own `disabled` flag. The date widget disables
// its calendar trigger as well, and a <button> is not reached by an "input, select" selector —
// naming the date-picker's own buttons rather than every button keeps SlimSelect's generated
// markup out of it.
const TOGGLEABLE_WIDGET_PARTS = "input, select, button[data-date-picker-target]"

export default class extends Controller {
  static targets = ["dropdownOptions", "optionsText", "defaultInput", "defaultSelect"]
  static values = { showFor: { type: String, default: "dropdown" } }

  connect() {
    this.toggle()
  }

  toggle() {
    const type = this.fieldType()

    if (this.hasDropdownOptionsTarget) {
      this.dropdownOptionsTarget.classList.toggle("hidden", type !== this.showForValue)
    }

    this.defaultInputTargets.forEach((wrapper) => {
      const matches = wrapper.dataset.fieldTypes.split(" ").includes(type)
      wrapper.classList.toggle("hidden", !matches)
      wrapper.querySelectorAll(TOGGLEABLE_WIDGET_PARTS).forEach((input) => {
        input.disabled = !matches
        // SlimSelect renders its own box and does not observe the native disabled flag.
        const select = this.application.getControllerForElementAndIdentifier(input, "select")
        if (select) matches ? select.slimselect.enable() : select.slimselect.disable()
      })
    })

    if (type === "dropdown") this.syncOptions()
  }

  // Mirror the options textarea into the default-value select, keeping the current choice
  // when its option still exists. The blank "kein Standardwert" row is kept by its data-keep
  // marker rather than by position: SlimSelect appends a synthetic placeholder option of its
  // own, so options[0] is not reliably ours.
  syncOptions() {
    if (!this.hasDefaultSelectTarget || !this.hasOptionsTextTarget) return

    const select = this.defaultSelectTarget
    const current = select.value
    const keep = [...select.options].filter((option) => option.dataset.keep === "true")
    const options = this.optionsTextTarget.value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    select.replaceChildren(...keep)
    options.forEach((value) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = value
      option.selected = value === current
      select.appendChild(option)
    })

    // SlimSelect snapshots the options at connect; tell it the DOM changed.
    this.application.getControllerForElementAndIdentifier(select, "select")?.refresh()
  }

  fieldType() {
    const select = this.element.querySelector("select[name*='field_type']")
    return select ? select.value : ""
  }
}
